import type { PhysicsBall, BallEvent, StepContext } from './types';
import {
  GRAVITY_BASE,
  VELOCITY_DAMPING,
  DESCENT_DURATION_MS,
  CONSUMPTION_DISTANCE,
  WOBBLE_FREQ,
  DESCENT_LIFT_AMPLITUDE,
  ABSORPTION_MAX_DIST,
  ENTRANCE_DURATION_MS,
  IMPULSE_DECEL,
  TRAIL_CHANCES,
  TRAIL_LIFETIMES,
  LANE_TRAIL_COLORS,
  SUCKED_TRAIL_COLOR,
  HANDOFF_BIN_SUCK_TRAIL,
} from './constants';

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/**
 * Pure physics step function. Mutates ball in-place and returns side-effect events.
 * NEVER makes business decisions — only advances position based on visualState.
 */
export function stepBall(ball: PhysicsBall, ctx: StepContext): BallEvent[] {
  const events: BallEvent[] = [];
  const { speed, ts, laneWidth, laneHeight, humanTarget, showTrails, gravityStrength, reducedMotion } = ctx;
  const pullMult = reducedMotion ? 2.35 : 1;
  const trailChanceMult = reducedMotion ? 0.25 : 1;

  switch (ball.visualState) {
    case 'idle':
    case 'thinking': {
      // Wobble vertical sine wave
      ball.wobble += WOBBLE_FREQ * speed;
      const targetVx = ball.visualState === 'thinking' ? 0.05 : 0.02;
      ball.vx += (targetVx - ball.vx) * 0.08 * speed;
      ball.vy += (Math.sin(ball.wobble) * 0.18 - ball.vy) * 0.12 * speed;

      // Apply impulse from message activity
      if (Math.abs(ball.impulseVx) > 0.01) {
        ball.vx += ball.impulseVx * 0.08 * speed;
        ball.impulseVx *= IMPULSE_DECEL;
      } else {
        ball.impulseVx = 0;
      }

      ball.x += ball.vx * speed;
      ball.y += ball.vy * speed;

      // Clamp within lane (never exit right)
      const r = ball.radius;
      ball.x = clamp(ball.x, r + 2, laneWidth - r - 2);
      ball.y = clamp(ball.y, r, laneHeight - r);

      // Trail spawn (idle only, not thinking)
      const trailChance = TRAIL_CHANCES[ball.visualState] ?? 0;
      if (showTrails && trailChance > 0 && Math.random() < trailChance) {
        events.push({
          type: 'spawn_trail',
          lane: ball.lane,
          x: ball.x,
          y: ball.y,
          color: LANE_TRAIL_COLORS[ball.lane] ?? '#6366F1',
          life: TRAIL_LIFETIMES[ball.visualState] ?? 350,
        });
      }
      break;
    }

    case 'sucked': {
      if (!humanTarget) break;

      const trailHue =
        ctx.suckTrailColor ?? HANDOFF_BIN_SUCK_TRAIL[ball.suckTargetBin ?? 'human'] ?? SUCKED_TRAIL_COLOR;

      const dx = humanTarget.x - ball.x;
      const dy = humanTarget.y - ball.y;
      const dist = Math.hypot(dx, dy) || 1;
      const g = GRAVITY_BASE * gravityStrength;
      const pull = g * (1 + 80 / Math.max(dist, 24)) * pullMult;
      // Na boca, sucção ligeiramente mais suave (protótipo: aproxima sem “teleport”)
      const subPull = ball.suckSubPhase === 'mouth' ? 0.82 : 1;

      ball.vx += (dx / dist) * pull * subPull * speed;
      ball.vy += (dy / dist) * pull * subPull * speed;
      ball.vx *= VELOCITY_DAMPING;
      ball.vy *= VELOCITY_DAMPING;
      ball.x += ball.vx * speed;
      ball.y += ball.vy * speed;

      // Scale/opacity absorption
      const closeness = clamp(1 - dist / ABSORPTION_MAX_DIST, 0, 1);
      ball.scale = 1 - closeness * 0.85;
      ball.opacity = 1 - closeness * 0.95;

      // Trail (cor do bin; duas amostras como na descida — rasto mais contínuo)
      if (showTrails) {
        const chance = (TRAIL_CHANCES.sucked ?? 0.5) * trailChanceMult;
        const life = TRAIL_LIFETIMES.sucked ?? 450;
        const pushTrail = (ox: number, oy: number) => {
          events.push({ type: 'spawn_trail', lane: ball.lane, x: ball.x + ox, y: ball.y + oy, color: trailHue, life });
        };
        if (Math.random() < chance) {
          pushTrail((Math.random() - 0.5) * 8, (Math.random() - 0.5) * 8);
        }
        if (Math.random() < chance * 0.65) {
          pushTrail((Math.random() - 0.5) * 10, (Math.random() - 0.5) * 10);
        }
      }

      // Só contabiliza após a fase "settle" (boca → corpo) ou sucção clássica (Humano na lane)
      const onTarget =
        ball.suckSubPhase == null
          ? dist < CONSUMPTION_DISTANCE
          : ball.suckSubPhase === 'settle' && dist < CONSUMPTION_DISTANCE;
      if (onTarget) {
        events.push({ type: 'consumed', leadId: ball.leadId, lane: ball.lane });
      }
      break;
    }

    case 'descending': {
      if (!ball.transitionMeta || ball.transitionMeta.type !== 'descend') break;

      const { startTime, fromPos, targetLane } = ball.transitionMeta;
      const p = clamp((ts - startTime) / DESCENT_DURATION_MS, 0, 1);

      // Horizontal: arco em plano (ease-out) — esquerda do centro → rasto/navegação à direita;
      // à direita do centro → aproxima o centro do eixo, para aterragem mapear ao meio da lane de baixo (não na extremidade).
      const w = ctx.laneWidth;
      const r = ball.radius;
      const pad = r + 2;
      const fromX = fromPos.x;
      const cx = w / 2;
      const left = fromX < cx;
      const maxX = w - pad;
      const easeLateral = 1 - (1 - p) * (1 - p);
      let xTarget: number;
      if (left) {
        const headroom = maxX - fromX;
        xTarget = maxX > fromX ? Math.min(maxX, fromX + Math.min(100, headroom * 0.55)) : fromX;
      } else {
        xTarget = cx;
      }
      ball.x = fromX + (xTarget - fromX) * easeLateral;

      // Vertical: arc UP then DOWN past lane bottom
      const lift = -DESCENT_LIFT_AMPLITUDE * Math.sin(Math.PI * p);
      const drop = (laneHeight + 80) * p * p;
      ball.y = fromPos.y + lift + drop;

      // Scale/opacity fade
      ball.scale = 1 - p * 0.2;
      ball.opacity = 1 - Math.max(0, (p - 0.7) / 0.3) * 0.9;

      // Rasto na descida — duas amostras por frame com alta probabilidade (faixa visível)
      if (showTrails) {
        const chance = TRAIL_CHANCES.descending ?? 0.94;
        const targetColor = targetLane ? (LANE_TRAIL_COLORS[targetLane] ?? '#10B981') : '#10B981';
        const life = TRAIL_LIFETIMES.descending ?? 520;
        const spawn = (ox: number, oy: number) => {
          events.push({
            type: 'spawn_trail',
            lane: ball.lane,
            x: ball.x + ox,
            y: ball.y + oy,
            color: targetColor,
            life,
          });
        };
        if (Math.random() < chance) {
          spawn((Math.random() - 0.5) * 10, (Math.random() - 0.5) * 10);
        }
        if (Math.random() < chance * 0.58) {
          spawn((Math.random() - 0.5) * 14, (Math.random() - 0.5) * 14);
        }
      }

      // Complete
      if (p >= 1 && targetLane) {
        events.push({ type: 'descend_complete', leadId: ball.leadId, targetLane });
      }
      break;
    }

    case 'entering': {
      if (!ball.transitionMeta || ball.transitionMeta.type !== 'entrance') break;

      const { startTime, fromPos, toPos } = ball.transitionMeta;
      const p = clamp((ts - startTime) / ENTRANCE_DURATION_MS, 0, 1);

      // Ease-out slide: extremos fixos (não reutilizar ball.x, que muda a cada frame)
      const end = toPos ?? { x: ball.x, y: fromPos.y };
      const ease = 1 - (1 - p) * (1 - p); // ease-out quad
      ball.x = fromPos.x + (end.x - fromPos.x) * ease;

      // Wobble starts during entrance
      ball.wobble += WOBBLE_FREQ * speed * 0.5;
      ball.vy += (Math.sin(ball.wobble) * 0.1 - ball.vy) * 0.08 * speed;
      ball.y += ball.vy * speed;
      ball.y = clamp(ball.y, ball.radius, laneHeight - ball.radius);

      ball.opacity = clamp(p * 1.5, 0, 1);
      ball.scale = 0.6 + 0.4 * ease;

      if (p >= 1) {
        ball.visualState = ball.pendingThinking ? 'thinking' : 'idle';
        ball.pendingThinking = false;
        ball.transitionMeta = undefined;
        ball.opacity = 1;
        ball.scale = 1;
      }
      break;
    }

    case 'exiting': {
      ball.opacity -= 0.03 * speed;
      ball.scale -= 0.01 * speed;
      if (ball.opacity <= 0) {
        ball.opacity = 0;
        events.push({ type: 'exit_complete', leadId: ball.leadId });
      }
      break;
    }
  }

  return events;
}
